import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../../config";
import { Bot, MessageCircle, Send, Sparkles, X, ThumbsUp, ThumbsDown } from "lucide-react";

export const ChatWidget = ({ t, language, userEmail }) => {
  const [isOpen, setIsOpen] = useState(false);
  // V218.4 — 7-day localStorage cooldown (not session). Once hidden, the
  // widget stays gone across tabs / days so we don't pester returning
  // visitors. The same chat is always reachable from the in-page chat
  // surface (/dashboard chat tab, FAQ, etc.) — this only hides the
  // floating bubble.
  const HIDE_KEY = "trp_chat_widget_hidden_at";
  const HIDE_MS = 7 * 24 * 60 * 60 * 1000;
  const [hidden, setHidden] = useState(() => {
    try {
      const ts = Number(localStorage.getItem(HIDE_KEY));
      return !!ts && Date.now() - ts < HIDE_MS;
    } catch { return false; }
  });
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);
  
  const hideWidget = () => {
    setIsOpen(false);
    setHidden(true);
    try { localStorage.setItem(HIDE_KEY, String(Date.now())); } catch { /* ignore */ }
  };
  
  // Initialize with greeting
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: 'greeting',
        type: 'bot',
        text: t.chat_greeting
      }]);
    }
  }, [t.chat_greeting, messages.length]);
  
  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      type: 'user',
      text: userMessage
    }]);
    
    // Add loading indicator
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        language: language,
        session_id: sessionId,
        email: userEmail
      });
      
      setSessionId(response.data.session_id);
      
      // Add bot response
      setMessages(prev => [...prev, {
        id: response.data.message_id,
        type: 'bot',
        text: response.data.response
      }]);
      
      // Add special offer if present
      if (response.data.special_offer && response.data.special_offer.show_offer) {
        setMessages(prev => [...prev, {
          id: `offer-${Date.now()}`,
          type: 'offer',
          offer: response.data.special_offer
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'bot',
        text: 'Sorry, something went wrong. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const sendFeedback = async (messageId, rating) => {
    // Optimistic UI — mark the message rated immediately, then fire
    // the request. Failures are logged but not surfaced (graceful).
    setMessages((prev) => prev.map((m) =>
      m.id === messageId ? { ...m, _rated: rating } : m,
    ));
    try {
      await axios.post(`${API}/chat/feedback`, {
        message_id: messageId, rating, note: "",
      });
    } catch (err) {
      console.error("Chat feedback failed:", err);
    }
  };
  
  if (hidden) return null;

  return (
    <>
      {/* Chat Toggle Button — with a tiny close (×) badge so users can fully dismiss it */}
      <div className="fixed bottom-24 right-6 z-40">
        <button
          data-testid="chat-toggle"
          onClick={() => setIsOpen(!isOpen)}
          className="w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all flex items-center justify-center"
          aria-label={t.chat_title || "Chat"}
        >
          {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        </button>
        {!isOpen && (
          <button
            type="button"
            data-testid="chat-widget-hide"
            onClick={hideWidget}
            aria-label="Hide chat"
            title="Skryť chat"
            className="absolute -top-1 -right-1 w-5 h-5 bg-zinc-900 text-white border border-zinc-700 rounded-full flex items-center justify-center hover:bg-zinc-800 shadow"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      
      {/* Chat Window */}
      {isOpen && (
        <div 
          data-testid="chat-window"
          className="fixed bottom-40 right-6 w-96 h-[500px] bg-white border border-gray-200 shadow-2xl flex flex-col z-40 animate-fade-in"
        >
          {/* Header */}
          <div className="p-4 bg-blue-600 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold">{t.chat_title}</h3>
                <p className="text-xs text-blue-100">{t.chat_subtitle}</p>
              </div>
            </div>
          </div>
          
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.type === 'offer' ? (
                  // Special Offer Card
                  <div 
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 p-4 rounded-lg text-white"
                    data-testid="chat-offer"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5" />
                      <span className="font-bold">Special Offer!</span>
                    </div>
                    <p className="text-sm mb-3">{msg.offer.message}</p>
                    <div className="flex items-center justify-between">
                      <div className="bg-white/20 px-3 py-1 rounded font-mono font-bold">
                        {msg.offer.discount_code}
                      </div>
                      <a
                        href="#pricing"
                        onClick={() => setIsOpen(false)}
                        className="bg-white text-green-600 px-4 py-2 rounded font-semibold text-sm hover:bg-green-50 transition-colors"
                      >
                        {msg.offer.cta_text}
                      </a>
                    </div>
                    <p className="text-xs mt-2 opacity-75">
                      Valid for {msg.offer.expires_hours}h
                    </p>
                  </div>
                ) : (
                  <div className={`max-w-[80%] ${msg.type === 'user' ? 'order-2' : ''}`}>
                    {msg.type === 'bot' && (
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                          <Bot className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-500">TopRankPulse AI</span>
                      </div>
                    )}
                    <div
                      className={`p-3 rounded-lg ${
                        msg.type === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                    </div>
                    {/* Feedback buttons — only on real bot replies (not
                        the local greeting which has no server-side id). */}
                    {msg.type === 'bot' && msg.id !== 'greeting' && (
                      <div className="flex items-center gap-1 mt-1 ml-8" data-testid={`chat-feedback-row-${msg.id}`}>
                        {msg._rated ? (
                          <span className="text-[10px] text-gray-500" data-testid={`chat-feedback-thanks-${msg.id}`}>
                            Ďakujem za feedback {msg._rated === 'up' ? '👍' : '👎'}
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={() => sendFeedback(msg.id, 'up')}
                              data-testid={`chat-feedback-up-${msg.id}`}
                              title="Užitočná odpoveď"
                              className="p-1 text-gray-400 hover:text-emerald-600 transition-colors"
                            ><ThumbsUp className="w-3 h-3" /></button>
                            <button
                              onClick={() => sendFeedback(msg.id, 'down')}
                              data-testid={`chat-feedback-down-${msg.id}`}
                              title="Nepomohlo"
                              className="p-1 text-gray-400 hover:text-rose-600 transition-colors"
                            ><ThumbsDown className="w-3 h-3" /></button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="max-w-[80%]">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <Bot className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-xs text-gray-500">{t.chat_thinking}</span>
                  </div>
                  <div className="bg-gray-100 p-3 rounded-lg">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                data-testid="chat-input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t.chat_placeholder}
                className="flex-1 px-3 py-2 border border-gray-200 focus:border-blue-600 focus:outline-none text-sm bg-white text-gray-900 placeholder-gray-400"
                disabled={isLoading}
              />
              <button
                data-testid="chat-send"
                onClick={handleSend}
                disabled={isLoading || !inputValue.trim()}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
